import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Paper,
  Grid,
  useTheme,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from '@mui/lab';
import {
  Assessment,
  Psychology,
  DirectionsWalk,
  Home,
  Medication,
  BarChart,
  Assignment,
  CheckCircle,
  Warning,
  Error,
  Timer,
  Person,
  LocalHospital,
  Lightbulb,
} from '@mui/icons-material';
import { PieChart, Pie, Cell, BarChart as RechartsBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const riskData = [
  { name: 'Low Risk', value: 30, color: '#4caf50' },
  { name: 'Moderate Risk', value: 45, color: '#ff9800' },
  { name: 'High Risk', value: 25, color: '#f44336' },
];

const interventionData = [
  { name: 'Exercise', success: 85, color: '#2196f3' },
  { name: 'Environment', success: 75, color: '#4caf50' },
  { name: 'Medication', success: 70, color: '#ff9800' },
  { name: 'Education', success: 90, color: '#9c27b0' },
];

const AssessmentProcessGuide = () => {
  const theme = useTheme();

  const ProcessStep = ({ icon: Icon, title, content, importance, timing }) => (
    <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Icon sx={{ mr: 1, color: theme.palette.primary.main }} />
        <Typography variant="h6" color="primary">{title}</Typography>
      </Box>
      <Typography variant="body2" paragraph>{content}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 'auto' }}>
        <Chip 
          label={importance} 
          size="small" 
          color="primary" 
          sx={{ mr: 1 }} 
        />
        <Typography variant="caption" color="text.secondary">
          {timing}
        </Typography>
      </Box>
    </Paper>
  );

  return (
    <Box>
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom color="primary">
            Assessment Process Guide
          </Typography>
          
          {/* Timeline Process */}
          <Timeline position="alternate">
            <TimelineItem>
              <TimelineSeparator>
                <TimelineDot color="primary">
                  <Assessment />
                </TimelineDot>
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent>
                <Typography variant="h6" component="span">
                  Initial Screening
                </Typography>
                <Typography>Comprehensive baseline assessment</Typography>
              </TimelineContent>
            </TimelineItem>

            <TimelineItem>
              <TimelineSeparator>
                <TimelineDot color="secondary">
                  <Psychology />
                </TimelineDot>
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent>
                <Typography variant="h6" component="span">
                  Detailed Assessment
                </Typography>
                <Typography>Physical, cognitive, and environmental evaluation</Typography>
              </TimelineContent>
            </TimelineItem>

            <TimelineItem>
              <TimelineSeparator>
                <TimelineDot color="warning">
                  <BarChart />
                </TimelineDot>
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent>
                <Typography variant="h6" component="span">
                  Risk Analysis
                </Typography>
                <Typography>Risk level determination</Typography>
              </TimelineContent>
            </TimelineItem>

            <TimelineItem>
              <TimelineSeparator>
                <TimelineDot color="info">
                  <Assignment />
                </TimelineDot>
              </TimelineSeparator>
              <TimelineContent>
                <Typography variant="h6" component="span">
                  Intervention Planning
                </Typography>
                <Typography>Customized prevention strategy</Typography>
              </TimelineContent>
            </TimelineItem>
          </Timeline>

          {/* Process Details Grid */}
          <Grid container spacing={3} sx={{ mt: 4 }}>
            <Grid item xs={12} md={6}>
              <ProcessStep
                icon={Assessment}
                title="1. Initial Assessment"
                content="Comprehensive evaluation including fall history, current mobility status, and basic health assessment."
                importance="Critical"
                timing="Day 1"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ProcessStep
                icon={Psychology}
                title="2. Detailed Evaluation"
                content="In-depth assessment of physical capabilities, cognitive function, and environmental factors."
                importance="Essential"
                timing="Days 1-2"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ProcessStep
                icon={BarChart}
                title="3. Risk Analysis"
                content="Data analysis and risk level determination based on assessment results."
                importance="High Priority"
                timing="Day 2-3"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ProcessStep
                icon={Assignment}
                title="4. Strategy Development"
                content="Creation of personalized intervention plan based on identified risks."
                importance="Critical"
                timing="Days 3-4"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Infographic Card */}
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom color="primary">
            Assessment Insights
          </Typography>
          
          <Grid container spacing={4}>
            {/* Risk Distribution */}
            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Risk Distribution
                </Typography>
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={riskData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {riskData.map((entry, index) => (
                          <Cell key={index} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                  {riskData.map((entry, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          backgroundColor: entry.color,
                          mr: 1,
                          borderRadius: '50%',
                        }}
                      />
                      <Typography variant="caption">
                        {entry.name}: {entry.value}%
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>

            {/* Intervention Success Rates */}
            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Intervention Success Rates
                </Typography>
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsBarChart
                      data={interventionData}
                      layout="vertical"
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" domain={[0, 100]} />
                      <YAxis dataKey="name" type="category" />
                      <Tooltip />
                      <Bar dataKey="success" fill="#8884d8">
                        {interventionData.map((entry, index) => (
                          <Cell key={index} fill={entry.color} />
                        ))}
                      </Bar>
                    </RechartsBarChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>

            {/* Key Indicators */}
            <Grid item xs={12}>
              <Paper elevation={3} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Key Assessment Indicators
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <Person color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Physical Factors" 
                          secondary="Balance, strength, gait pattern"
                        />
                      </ListItem>
                    </List>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <LocalHospital color="secondary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Medical Factors" 
                          secondary="Medications, chronic conditions"
                        />
                      </ListItem>
                    </List>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <Lightbulb color="warning" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Environmental Factors" 
                          secondary="Home hazards, lighting, layout"
                        />
                      </ListItem>
                    </List>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AssessmentProcessGuide;
